import React from 'react';
import {
    Autocomplete,
    AutocompleteProps,
    AutocompleteRenderInputParams,
    CircularProgress,
    InputAdornment,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import BaseFormControl from '@/components/form/BaseFormControl';

export interface BaseAutocompleteProps<
    T,
    Multiple extends boolean = false,
    DisableClearable extends boolean = false,
    FreeSolo extends boolean = false,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'options' | 'renderInput'> {
    options: T[];
    loading?: boolean;
    label?: string;
    renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
}

export default function BaseAutocomplete<
    T,
    Multiple extends boolean = false,
    DisableClearable extends boolean = false,
    FreeSolo extends boolean = false,
>({
    options,
    loading = false,
    label,
    renderInput,
    ...props
}: BaseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
    const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
        <TextField
            {...params}
            label={label}
            placeholder={label}
            slotProps={{
                input: {
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {loading && (
                                <InputAdornment position="end">
                                    <CircularProgress size={26} />
                                </InputAdornment>
                            )}
                            {params.InputProps.endAdornment}
                        </>
                    ),
                },
            }}
        />
    );

    return (
        <BaseFormControl fullWidth variant="outlined">
            <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
                {...props}
                options={options}
                loading={loading}
                loadingText={
                    <>
                        <LinearProgress />
                        <Typography sx={{ ml: 0.5, mt: 1.5 }}>Loading...</Typography>
                    </>
                }
                noOptionsText={props.noOptionsText ?? 'No options available.'}
                renderInput={renderInput ?? defaultRenderInput}
                slots={{ paper: Paper }}
                slotProps={{
                    paper: {
                        sx: { maxHeight: '30vh', overflow: 'auto' },
                    },
                    listbox: {
                        sx: { maxHeight: '30vh', overflowY: 'auto' },
                    },
                }}
            />
        </BaseFormControl>
    );
}
